// Getting a shared hall screen back to the start, three ways.
//
// HomeReset is the clock on an ending. After Submit the thanks stage would
// otherwise sit until somebody taps Done, and a queue behind them means the
// next person sees the last person's thank-you. Five seconds is long enough to
// read it; a won prize gets RESULT_RESET_MS instead.
//
// IdleHome is the clock on a middle. Somebody who starts a form and walks off
// leaves it half-typed on a screen the next person will pick up, and no ending
// ever arrives to clear it. Any use of the screen restarts that clock.
//
// The top-left hit is under both, for a crew member who should not wait for
// either: three taps, no label, not in the tab order.

import { useEffect, useRef, type ReactNode } from "react";

export const HOME_RESET_MS = 5000;

/**
 * Longer, for a screen somebody is meant to read a result off.
 *
 * Five seconds is the right length for "You are registered", which carries no
 * information. A won prize and a claim code to quote at the stand is the other
 * kind of screen: whipping it away before it has been read is the whole of the
 * value of the spin gone.
 */
export const RESULT_RESET_MS = 12000;

const TRIPLE_WINDOW_MS = 800;

export function HomeReset({
  onHome,
  afterMs = HOME_RESET_MS,
  crewResetEnabled = true,
}: {
  onHome: () => void;
  afterMs?: number;
  crewResetEnabled?: boolean;
}): ReactNode {
  useEffect(() => {
    const timer = window.setTimeout(onHome, afterMs);
    return () => window.clearTimeout(timer);
  }, [onHome, afterMs]);

  return crewResetEnabled ? <HomeHit onHome={onHome} /> : null;
}

/**
 * How long a hall screen sits untouched before it wipes and goes home.
 *
 * Two minutes. Long enough that somebody working through a consent paragraph
 * properly is not punished for reading it, short enough that a walk-away does
 * not leave the next person looking at a stranger's half-typed name and
 * mailbox. Any touch, key, scroll or wheel restarts it, so the only way to
 * reach the end of it is to stop using the screen.
 *
 * It is not armed while a registration is being written. The answer to that
 * request is already on its way and onsite.ts has nowhere for `writing` to go
 * but `done` or `refused`.
 */
export const IDLE_HOME_MS = 120_000;

/**
 * Back to the start after a spell of nothing, on a screen we own.
 *
 * Returning home unmounts the form, which is what actually clears it: the
 * answers live in the step component's state and nowhere else, so a walk-away
 * takes them with it.
 */
export function IdleHome({
  onHome,
  afterMs = IDLE_HOME_MS,
  crewResetEnabled = true,
}: {
  onHome: () => void;
  afterMs?: number;
  crewResetEnabled?: boolean;
}): ReactNode {
  // Held in a ref so an inline arrow at the call site does not re-arm the
  // clock on every render, which would be a timer that never fires.
  const home = useRef(onHome);
  home.current = onHome;

  useEffect(() => {
    let timer = 0;
    const arm = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => home.current(), afterMs);
    };
    arm();
    // Capture phase: a tap a control stops from bubbling is still somebody
    // using the screen, and typing into a field is the commonest case of it.
    for (const name of ACTIVITY) {
      document.addEventListener(name, arm, { capture: true, passive: true });
    }
    return () => {
      window.clearTimeout(timer);
      for (const name of ACTIVITY) document.removeEventListener(name, arm, { capture: true });
    };
  }, [afterMs]);

  return crewResetEnabled ? <HomeHit onHome={onHome} /> : null;
}

const ACTIVITY = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;

/** The unlabelled top-left corner. Three taps, not in the tab order. */
function HomeHit({ onHome }: { onHome: () => void }): ReactNode {
  const clicks = useRef({ count: 0, at: 0 });

  return (
    <button
      type="button"
      className="xp-home-hit"
      aria-hidden="true"
      tabIndex={-1}
      onClick={() => {
        const now = Date.now();
        if (now - clicks.current.at > TRIPLE_WINDOW_MS) clicks.current = { count: 0, at: now };
        clicks.current.count += 1;
        clicks.current.at = now;
        if (clicks.current.count >= 3) {
          clicks.current.count = 0;
          onHome();
        }
      }}
    />
  );
}
